/**
 * VerificarCertificado - Página de verificação de certificados de autoria
 * 
 * Permite validar a autenticidade de um certificado através do hash
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Shield, 
  CheckCircle2, 
  XCircle, 
  BookOpen, 
  Users, 
  Calendar,
  Loader2,
  ArrowLeft,
  Award,
  Fingerprint
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerificarCertificado() {
  const { hash } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hash) {
      verifyCertificate();
    }
  }, [hash]);

  const verifyCertificate = async () => {
    try {
      setLoading(true);
      // O backend deve ter um endpoint público para verificar
      const response = await axios.get(
        `${API_URL}/api/coautoria/certificate/verify/${hash}`
      );
      setCertificate(response.data);
      setError(null);
    } catch (err) {
      console.error('Verification error:', err);
      if (err.response?.status === 404) {
        setError('Certificado não encontrado. O hash informado é inválido.');
      } else {
        setError('Erro ao verificar certificado. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 text-amber-400 animate-spin mx-auto" />
          <p className="text-gray-400">Verificando certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Voltar ao início
        </Link>

        {error ? (
          /* Error State */
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Certificado Inválido</h1>
            <p className="text-red-400 mb-6">{error}</p>
            <div className="bg-black/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Hash verificado:</p>
              <code className="text-amber-400 font-mono">{hash}</code>
            </div>
            <Button asChild variant="outline" className="border-white/20 text-white">
              <Link to="/">Ir para o início</Link>
            </Button>
          </div>
        ) : certificate ? (
          /* Success State */
          <div className="space-y-6">
            {/* Verification Badge */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Certificado Válido</h1>
              <p className="text-green-400">Este certificado é autêntico e foi verificado com sucesso.</p>
            </div>

            {/* Certificate Details */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-amber-500/20 rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="h-2 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
              
              <div className="p-6 space-y-6">
                {/* Title */}
                <div className="text-center">
                  <Award className="h-10 w-10 text-amber-400 mx-auto mb-2" />
                  <h2 className="text-sm text-gray-400 uppercase tracking-wider">Certificado de Autoria</h2>
                </div>

                {/* Book Info */}
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-400 mb-2">
                    <BookOpen className="h-4 w-4" />
                    <span className="text-sm">Obra Literária</span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white">{certificate.book_title}</h3>
                  <div className="flex items-center justify-center gap-4 mt-2 text-sm text-gray-500">
                    <span>{certificate.total_words?.toLocaleString()} palavras</span>
                    <span>•</span>
                    <span>{certificate.total_chapters} capítulos</span>
                  </div>
                </div>

                {/* Authors */}
                <div>
                  <div className="flex items-center gap-2 text-gray-400 mb-3">
                    <Users className="h-4 w-4" />
                    <span className="text-sm uppercase tracking-wider">Autores</span>
                  </div>
                  <div className="space-y-2">
                    {certificate.authors?.map((author, index) => (
                      <div 
                        key={index}
                        className="bg-white/5 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                            {(author.user_name || 'A').charAt(0)}
                          </div>
                          <div>
                            <span className="font-medium text-white">{author.user_name}</span>
                            <Badge variant="outline" className="ml-2 text-xs border-amber-500/30 text-amber-400/80">
                              {!author.invited_by ? 'Criador' : 'Co-autor'}
                            </Badge>
                          </div>
                        </div>
                        <span className="text-xl font-bold text-amber-400">{author.contribution_percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Verification Info */}
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Fingerprint className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-400">Hash de verificação:</span>
                  </div>
                  <code className="block bg-black/30 rounded p-2 text-amber-400 font-mono text-sm break-all">
                    {certificate.verification_hash}
                  </code>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>Emitido em: {formatDate(certificate.generated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="h-2 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500" />
            </div>

            {/* Security Notice */}
            <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3">
              <Shield className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-400">
                <p className="font-medium text-white mb-1">Verificação de Segurança</p>
                <p>Este certificado foi validado através de hash criptográfico. Os dados apresentados são autênticos e não foram alterados desde a emissão.</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
